import { formatDateV2 } from 'util/formatDate';
import { Box, Typography } from '@mui/material';
import React, { ReactElement, useMemo, useState } from 'react';
import clsx from 'clsx';
import Color from 'color';
import { colorLightGrey } from 'customColors';
import ArrowRightIcon from '../../../assets/icons/arrow_right.svg';

export interface CalendarProps {
  selected?: Date | null;
  maxDate?: Date;
  minDate?: Date;
  onChange: (date: Date) => void;
  uncontrolled?: boolean;
}

const DAY = 1000 * 60 * 60 * 24;
type DateObj = {
  year: number;
  month: number;
  day: number;
};

export const Calendar = ({
  selected,
  maxDate,
  minDate,
  onChange,
  uncontrolled = false,
}: CalendarProps): ReactElement => {
  if (minDate) minDate.setHours(0, 0, 0, 0);

  if (maxDate) maxDate.setHours(0, 0, 0, 0);

  const [yearMonth, setYearMonth] = useState<Date>(() =>
    selected
      ? new Date(selected.getFullYear(), selected.getMonth())
      : new Date(new Date(Date.now() + DAY).setDate(1)),
  );

  const [isControlled] = useState(!uncontrolled);
  const [selectedState, setSelected] = useState<Date | null | undefined>(
    selected,
  );

  const [focusableIndex, setFocusableIndex] = useState<number | null>(null);

  const currentMonth = yearMonth.getMonth();
  const isPrevMonthNavDisabled =
    !!minDate && new Date(+yearMonth - DAY) < minDate;
  const isNextMonthNavDisabled =
    !!maxDate && new Date(yearMonth.getFullYear(), currentMonth + 1) > maxDate;

  const days = useMemo(() => {
    const dayOfWeekIndex = yearMonth.getDay();
    const numDaysInCurrentMonth = new Date(
      yearMonth.getFullYear(),
      yearMonth.getMonth() + 1,
      0,
    ).getDate();
    const numDaysInPrevMonth = new Date(+yearMonth - DAY).getDate();

    const year = yearMonth.getFullYear();
    const month = yearMonth.getMonth();

    const prevMonth = (month + 11) % 12;
    const prevMonthYear = month === 0 ? year - 1 : year;

    const nextMonth = (month + 1) % 12;
    const nextMonthYear = yearMonth.getMonth() === 1 ? year + 1 : year;

    const days: DateObj[] = [];
    for (let i = 1; i <= dayOfWeekIndex; i++) {
      const day = numDaysInPrevMonth - dayOfWeekIndex + i;
      days.push({ year: prevMonthYear, month: prevMonth, day });
    }
    for (let i = 1; i <= numDaysInCurrentMonth; i++)
      days.push({ year, month: yearMonth.getMonth(), day: i });
    for (let i = 1; days.length % 7; i++)
      days.push({ year: nextMonthYear, month: nextMonth, day: i });

    if (focusableIndex === null) {
      setFocusableIndex(
        new Date(Date.now() + DAY).getDate() + dayOfWeekIndex - 1,
      );
    } else if (focusableIndex >= days.length) {
      setFocusableIndex(focusableIndex - 7);
    }

    return days;
  }, [yearMonth]);

  const handleClickDate = ({ year, month, day }: DateObj) => {
    const date = new Date(year, month, day);
    if (minDate && date < minDate) return;
    if (maxDate && date > maxDate) return;

    onChange(date);
    if (!isControlled) setSelected(date);
    setFocusableIndex(
      days.findIndex(
        date => date.year === year && date.month === month && date.day === day,
      ),
    );
  };

  const selectedDay = {
    year: isControlled ? selected?.getFullYear() : selectedState?.getFullYear(),
    month: isControlled ? selected?.getMonth() : selectedState?.getMonth(),
    day: isControlled ? selected?.getDate() : selectedState?.getDate(),
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDay = {
    year: today.getFullYear(),
    month: today.getMonth(),
    day: today.getDate(),
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ userSelect: 'none' }}
      >
        <Box minWidth={160} textAlign="center">
          <Typography color="primary" style={{ fontWeight: 500 }}>
            {formatDateV2(yearMonth, { noCommas: true })}
          </Typography>
        </Box>
        <Box
          sx={{ display: 'flex', gap: 64, justifyContent: 'flex-end', mx: 24 }}
        >
          <ArrowRightIcon
            style={{
              transform: 'rotate(180deg)',
              opacity: isPrevMonthNavDisabled ? 0.5 : 1,
              cursor: isPrevMonthNavDisabled ? 'default' : 'pointer',
            }}
            {...(isPrevMonthNavDisabled
              ? {
                  disabled: true,
                  onClick: undefined,
                }
              : {
                  onClick: () =>
                    setYearMonth(
                      new Date(yearMonth.getFullYear(), currentMonth - 1),
                    ),
                })}
          />
          <ArrowRightIcon
            style={{
              opacity: isNextMonthNavDisabled ? 0.5 : 1,
              cursor: isNextMonthNavDisabled ? 'default' : 'pointer',
            }}
            {...(isNextMonthNavDisabled
              ? {
                  disabled: true,
                  onClick: undefined,
                }
              : {
                  onClick: () =>
                    setYearMonth(
                      new Date(yearMonth.getFullYear(), currentMonth + 1),
                    ),
                })}
          />
        </Box>
      </Box>

      <Box
        role="grid"
        mt={10}
        display="grid"
        gridTemplateColumns="repeat(7, 1fr)"
        gridTemplateRows="repeat(7, 1fr)"
        justifyItems="center"
        alignItems="center"
        style={{ userSelect: 'none' }}
        maxWidth="100%"
        overflow="hidden"
        pb={4}
        mb={-4}
        sx={{
          gridRowGap: 8,
          '& .head': {
            fontWeight: 500,
            textAlign: 'center',
            lineHeight: 1,
            paddingBottom: 16,
            width: '100%',
            marginBottom: -8,
            fontSize: 14,

            '&.weekday': {
              borderBottom: `1px solid ${colorLightGrey}`,
            },
          },
          '& .day': {
            width: 42,
            height: 42,
            borderRadius: 21,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            cursor: 'pointer',
            '&.selectable:hover': {
              // eslint-disable-next-line new-cap
              background: theme =>
                new Color(theme.palette.primary.main).alpha(0.3).string(),
            },
            '&.selected': {
              background: theme => theme.palette.primary.main,
              fontWeight: 600,
              color: theme => theme.palette.common.white,
              boxShadow: theme => `0 2px 8px ${theme.palette.primary.main}40`,
            },
            '&.disabled': {
              opacity: 0.3,
              cursor: 'not-allowed',
              color: theme => theme.palette.text.disabled,
            },
            '&.today': {
              fontWeight: 600,
              '&:not(.selected)': {
                padding: 5,
                border: theme => `1px solid ${theme.palette.primary.main}`,
              },
            },
            outline: 'none',
            '&:focus-visible': {
              outline: theme => `2px auto ${theme.palette.primary.main}`,
              outlineOffset: 6,
            },
          },
        }}
      >
        <Typography
          className="head"
          style={{
            width: 'calc(50% + 20px)',
            marginLeft: 'calc(50% - 20px)',
            paddingRight: 'calc(50% - 20px)',
          }}
        >
          Sun
        </Typography>
        <Typography className="head weekday">Mon</Typography>
        <Typography className="head weekday">Tue</Typography>
        <Typography className="head weekday">Wed</Typography>
        <Typography className="head weekday">Thu</Typography>
        <Typography className="head weekday">Fri</Typography>
        <Typography
          className="head"
          style={{
            width: 'calc(50% + 20px)',
            marginRight: 'calc(50% - 20px)',
            paddingLeft: 'calc(50% - 20px)',
          }}
        >
          Sat
        </Typography>
        {days.map((date, i) => {
          const asDate = new Date(date.year, date.month, date.day);

          const isSelectable =
            (!minDate || asDate >= minDate) && (!maxDate || asDate <= maxDate);

          const isSelected =
            selectedDay.year === date.year &&
            selectedDay.month === date.month &&
            selectedDay.day === date.day;

          const isToday =
            todayDay.year === date.year &&
            todayDay.month === date.month &&
            todayDay.day === date.day;

          return (
            // Using index as key is required to keep focus when changing pages with PageUp/PageDown keys
            <React.Fragment key={i}>
              <Typography
                color={
                  date.month === currentMonth && isSelectable
                    ? 'textPrimary'
                    : 'textSecondary'
                }
                className={clsx('day', {
                  selected: isSelected,
                  selectable: isSelectable,
                  disabled: !isSelectable,
                  today: isToday,
                })}
                onClick={() => handleClickDate(date)}
                onKeyDown={
                  i === focusableIndex
                    ? e => {
                        if (
                          [
                            'PageUp',
                            'PageDown',
                            'ArrowLeft',
                            'ArrowRight',
                            'ArrowUp',
                            'ArrowDown',
                          ].includes(e.key)
                        )
                          e.preventDefault();

                        let newFocusableIndex = focusableIndex;

                        if (e.key === 'PageUp' && !isPrevMonthNavDisabled) {
                          setYearMonth(
                            new Date(yearMonth.getFullYear(), currentMonth - 1),
                          );
                          const lastDayOfPrevMonth = new Date(+yearMonth - DAY);
                          const newMonthIsFourRows =
                            lastDayOfPrevMonth.getDate() === 28 &&
                            lastDayOfPrevMonth.getDay() === 6;
                          if (
                            focusableIndex > 34 ||
                            (focusableIndex > 27 && newMonthIsFourRows)
                          )
                            newFocusableIndex -= 7;
                        }

                        if (e.key === 'PageDown' && !isNextMonthNavDisabled) {
                          setYearMonth(
                            new Date(yearMonth.getFullYear(), currentMonth + 1),
                          );
                          const lastDayOfNextMonth = new Date(
                            new Date(
                              yearMonth.getFullYear(),
                              currentMonth + 2,
                            ).getTime() - DAY,
                          );
                          const newMonthIsFourRows =
                            lastDayOfNextMonth.getDate() === 28 &&
                            lastDayOfNextMonth.getDay() === 6;
                          if (
                            focusableIndex > 34 ||
                            (focusableIndex > 27 && newMonthIsFourRows)
                          )
                            newFocusableIndex -= 7;
                        }

                        if (e.key === 'ArrowLeft') {
                          newFocusableIndex = Math.max(
                            0,
                            newFocusableIndex - 1,
                          );
                        }

                        if (e.key === 'ArrowRight') {
                          newFocusableIndex = Math.min(
                            days.length - 1,
                            newFocusableIndex + 1,
                          );
                        }

                        if (e.key === 'ArrowUp' && newFocusableIndex > 7)
                          newFocusableIndex -= 7;

                        if (
                          e.key === 'ArrowDown' &&
                          newFocusableIndex + 7 < days.length
                        )
                          newFocusableIndex += 7;

                        setFocusableIndex(newFocusableIndex);
                        const parent = (e.target as HTMLElement)
                          .parentNode as HTMLElement;
                        const el = parent.children[
                          newFocusableIndex + 7
                        ] as HTMLParagraphElement;
                        el.focus();
                      }
                    : undefined
                }
                tabIndex={i === focusableIndex ? 0 : -1}
                aria-label={formatDateV2(
                  new Date(date.year, date.month, date.day),
                  { includeDate: true, includeDay: true },
                )}
                aria-disabled={!isSelectable}
              >
                <Box component="span">{date.day}</Box>
                {isToday && !isSelected && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 8,
                      lineHeight: 1,
                      fontWeight: 400,
                      color: 'primary.main',
                    }}
                  >
                    Today
                  </Box>
                )}
              </Typography>
            </React.Fragment>
          );
        })}
        {days.length < 42 &&
          Array.from(Array(42 - days.length), (_, i) => (
            <Typography key={42 - days.length + i}>&nbsp;</Typography>
          ))}
      </Box>
    </Box>
  );
};
